import React from 'react';
import { motion } from 'framer-motion';
import { format, differenceInDays, isValid, parseISO } from 'date-fns';
import { sl } from 'date-fns/locale';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { formatSlNumber } from '../utils/csvUtils';

const { FiClock, FiCheck, FiAlertCircle, FiEuro, FiUser, FiPackage } = FiIcons;

const PendingInvoices = ({ invoices, onMarkAsPaid }) => {
  // Safe date parsing function
  const parseSafeDate = (dateValue) => {
    try {
      if (!dateValue) return new Date();
      
      let date;
      if (typeof dateValue === 'string') {
        date = parseISO(dateValue);
      } else {
        date = new Date(dateValue);
      }
      
      if (!isValid(date)) {
        console.warn('Invalid date for urgency calculation:', dateValue);
        return new Date();
      }
      
      return date;
    } catch (error) {
      console.error('Date parsing error:', error, 'for value:', dateValue);
      return new Date();
    }
  };

  // Safe date formatting function
  const formatSafeDate = (dateValue) => {
    try {
      const date = parseSafeDate(dateValue);
      return format(date, 'd.M.yyyy', { locale: sl });
    } catch (error) {
      console.error('Date formatting error:', error, 'for value:', dateValue);
      return 'Napaka datuma';
    }
  };

  const getUrgencyLevel = (dueDate) => {
    try {
      const today = new Date();
      const due = parseSafeDate(dueDate);
      const daysOverdue = differenceInDays(today, due);

      if (daysOverdue > 7) return 'critical';
      if (daysOverdue > 0) return 'overdue';
      if (daysOverdue === 0) return 'today';
      return 'upcoming';
    } catch (error) {
      console.error('Urgency calculation error:', error);
      return 'upcoming';
    }
  };

  const getUrgencyStyles = (level) => {
    switch (level) {
      case 'critical': return 'border-l-red-600 bg-red-50';
      case 'overdue': return 'border-l-orange-500 bg-orange-50';
      case 'today': return 'border-l-yellow-500 bg-yellow-50';
      default: return 'border-l-[#3297c7] bg-blue-50';
    }
  };

  const getUrgencyText = (dueDate) => {
    try {
      const today = new Date();
      const due = parseSafeDate(dueDate);
      const daysOverdue = differenceInDays(today, due);

      if (daysOverdue > 0) {
        return `${daysOverdue}d zamude`;
      }
      if (daysOverdue === 0) {
        return 'Danes';
      }
      return formatSafeDate(dueDate);
    } catch (error) {
      console.error('Urgency text error:', error);
      return 'Napaka datuma';
    }
  };

  const getIntervalFull = (interval) => {
    switch (interval) {
      case 'monthly': return 'Mesečno';
      case 'quarterly': return 'Četrtletno';
      case 'half-yearly': return 'Polletno';
      case 'yearly': return 'Letno';
      default: return 'Mesečno';
    }
  };

  const getTotalAmount = (invoice) => {
    return invoice.total_amount || invoice.totalAmount || invoice.amount || 0;
  };

  // Sort pending invoices: first by date, then alphabetically by customer name
  const sortedInvoices = [...invoices].sort((a, b) => {
    try {
      const dateA = parseSafeDate(a.next_due || a.nextDue);
      const dateB = parseSafeDate(b.next_due || b.nextDue);

      // First sort by date
      if (dateA.getTime() !== dateB.getTime()) {
        return dateA - dateB;
      }

      // If dates are the same, sort alphabetically by customer name
      const nameA = (a.stranka || a.clientName || '').toLowerCase();
      const nameB = (b.stranka || b.clientName || '').toLowerCase();
      return nameA.localeCompare(nameB, 'sl');
    } catch (error) {
      console.error('Sorting error:', error);
      return 0;
    }
  });

  if (invoices.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-12"
      >
        <SafeIcon icon={FiClock} className="text-5xl text-gray-400 mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-[#4d504a] mb-1">Ni računov v plačilo</h3>
        <p className="text-gray-500 text-sm">Vsi računi so posodobljeni!</p>
      </motion.div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <h2 className="text-xl font-bold text-[#4d504a] mb-1">Računi v plačilo</h2>
        <p className="text-[#6e725c] text-sm">
          {invoices.length} račun{invoices.length === 1 ? '' : invoices.length === 2 ? 'a' : invoices.length <= 4 ? 'i' : 'ov'} potrebuje{invoices.length === 1 ? '' : 'jo'} pozornost
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
        {sortedInvoices.map((invoice, index) => {
          const urgencyLevel = getUrgencyLevel(invoice.next_due || invoice.nextDue);
          return (
            <motion.div
              key={invoice.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className={`bg-white rounded-lg shadow-md border-l-4 ${getUrgencyStyles(urgencyLevel)} p-4 hover:shadow-lg transition-shadow h-fit`}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center min-w-0 flex-1">
                  <SafeIcon icon={FiUser} className="text-[#6e725c] mr-2 flex-shrink-0 text-sm" />
                  <h3 className="font-semibold text-[#4d504a] text-sm truncate">
                    {invoice.stranka || invoice.clientName}
                  </h3>
                  {invoice.line_items && invoice.line_items.length > 0 && (
                    <div className="ml-1 relative flex-shrink-0">
                      <SafeIcon icon={FiPackage} className="text-[#6e725c] text-xs" />
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-3 w-3 flex items-center justify-center text-[9px]">
                        {invoice.line_items.length}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Amount */}
              <div className="flex items-center mb-3">
                <SafeIcon icon={FiEuro} className="text-[#80b238] mr-2 text-sm" />
                <div className="min-w-0 flex-1">
                  <span className="font-bold text-[#80b238] text-lg block">
                    {formatSlNumber(getTotalAmount(invoice))} €
                  </span>
                </div>
              </div>

              {/* Info row */}
              <div className="flex items-center justify-between mb-3 text-xs">
                <div className="flex items-center">
                  <SafeIcon icon={FiClock} className="text-[#6e725c] mr-1" />
                  <span className="text-[#4d504a]">{getIntervalFull(invoice.interval)}</span>
                </div>
                <div className="flex items-center">
                  <SafeIcon icon={FiAlertCircle} className="text-orange-500 mr-1" />
                  <span className={`font-medium ${
                    urgencyLevel === 'critical' ? 'text-red-600' : 
                    urgencyLevel === 'overdue' ? 'text-orange-600' : 
                    urgencyLevel === 'today' ? 'text-yellow-600' : 'text-[#3297c7]'
                  }`}>
                    {getUrgencyText(invoice.next_due || invoice.nextDue)}
                  </span>
                </div>
              </div>

              {/* Line items info */}
              {invoice.line_items && invoice.line_items.length > 0 && (
                <div className="mb-3">
                  <span className="text-xs text-[#6e725c]">
                    +{invoice.line_items.length} postavk{invoice.line_items.length === 1 ? 'a' : invoice.line_items.length <= 4 ? 'e' : ''}
                  </span>
                </div>
              )}

              {/* Description */}
              {invoice.description && (
                <p className="text-[#4d504a] text-xs mb-3 line-clamp-2">{invoice.description}</p>
              )}

              {/* Action button */}
              <motion.button
                onClick={() => onMarkAsPaid(invoice.id)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="w-full bg-[#80b238] text-white px-4 py-2 rounded font-medium hover:bg-[#6d9530] transition-colors flex items-center justify-center text-sm"
              >
                <SafeIcon icon={FiCheck} className="mr-2 text-sm" />
                Zabeleži plačilo
              </motion.button>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default PendingInvoices;